import ErrorMessage from "@/v2-ui/ErrorMessage"
import Dialog, { type DialogProps } from "@/v2-ui/dialog"
import { toast } from "@/v2-ui/toast"
import Button from "@/v2-backoffice-client/components/button"
import { describeCrmOutcomes } from "@/v2-backoffice-client/crm/crm.outcome.utils"
import { useTenantInvitationCreateManyMutation } from "@/v2-backoffice-client/tenant/invitation/create/tenant.invitation.create.many.mutation"
import InvitationCreateForm, {
  type InvitationCreateFormData,
} from "@/v2-backoffice-client/invitation/create/InvitationCreateForm"
import type { InvitationEmailTemplateValue } from "@/v2-backoffice-client/invitation/invitation.constants"
import UserMigratePromptDialog from "@/v2-backoffice-client/user/migration/prompt/UserMigratePromptDialog"
import {
  useUserMigratePrompt,
  withoutPromptedUsers,
} from "@/v2-backoffice-client/user/migration/prompt/user.migration.prompt.hooks"
import { useAppViewSheetOpener } from "@/v2-backoffice-client/app/app.utils"
import type { UserFragment } from "@/v2-backoffice-client/user/__types__/user.fragment"

export type InvitationCreateDialogProps = DialogProps

/** The batch minus the people the prompt steered to activation instead. */
function withoutUsers(data: InvitationCreateFormData, users: UserFragment[]) {
  const sections = data.sections
    .map((section) => ({
      ...section,
      rows: withoutPromptedUsers(section.rows, users),
    }))
    .filter((section) => section.rows.length > 0)
  return { ...data, sections }
}

function countRows(data: InvitationCreateFormData) {
  return data.sections.reduce((sum, section) => sum + section.rows.length, 0)
}

function InvitationCreateDialog(props: InvitationCreateDialogProps) {
  const { open, onOpenChange, ...dialogProps } = props

  const opener = useAppViewSheetOpener()
  const [createManyInvitations, { loading, error }] =
    useTenantInvitationCreateManyMutation()
  // Existing-but-not-activated users among the invitees get the login
  // migration instead of an invitation (UserMigratePromptDialog).
  const migratePrompt = useUserMigratePrompt<InvitationCreateFormData>()

  async function send(data: InvitationCreateFormData) {
    // Project overrides only exist for single-team batches (the form hides
    // the fields otherwise); multi-team sections get each team's own
    // project defaults.
    const singleTeam = data.sections.length === 1
    const result = await createManyInvitations({
      variables: {
        inputs: data.sections.flatMap((section) =>
          section.rows.map((row) => ({
            TenantId: section.tenant.Id,
            ProjectId: singleTeam ? data.ProjectId : undefined,
            AutoCreateProject: singleTeam ? data.AutoCreateProject : undefined,
            CountryCode: singleTeam ? data.CountryCode : undefined,
            Email: row.Email,
            FirstName: row.FirstName,
            LastName: row.LastName,
            UserTenantTypeCode: row.UserTenantTypeCode,
            MinuteLimit: row.MinuteLimit,
            SendImmediately: data.SendImmediately,
            Language: data.Language,
            // The literal→generated-enum bridge (see invitation.constants:
            // the app types template values as string literals so nothing
            // imports the enum at runtime).
            EmailTemplate: data.EmailTemplate as InvitationEmailTemplateValue,
          })),
        ),
        crm: { CreateContact: data.CreateContact },
      },
    })
    // errorPolicy is "all" app-wide, so a rejected batch (project not
    // linked to the team, unsupported country, …) resolves instead of
    // throwing — closing here would swallow the error banner above.
    if (result.errors?.length) return
    onOpenChange?.(false)

    const created = result.data?.createManyUserInvitations.Invitations ?? []
    const crmSummary = describeCrmOutcomes(
      result.data?.createManyUserInvitations.Crm ?? [],
    )
    toast({
      variant: "success",
      title: `${created.length} invitation${created.length === 1 ? "" : "s"} sent`,
      description:
        crmSummary ||
        "Invitees are on their HubSpot contacts as soon as the hourly sync runs.",
    })
    if (!opener.kind && created.length === 1) {
      opener.open("invitation", created[0].Id)
    }
  }

  const prompt = migratePrompt.prompt
  const remaining = prompt ? withoutUsers(prompt.data, prompt.users) : null

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange} {...dialogProps}>
        {/* Fixed header + footer with the form scrolling between them, so the
            Send button stays reachable however long the import gets. */}
        <Dialog.Content
          className="flex w-[960px] max-w-[96vw] flex-col overflow-hidden !top-[5vh] max-h-[90vh]"
          align="top"
        >
          <Dialog.Header>
            <Dialog.Title>Invite Users</Dialog.Title>
            <Dialog.Description>
              Group invitees by team — import a spreadsheet or add sections by
              hand. Imported teams match automatically; anything unmatched is
              created for you (customer included) when you send. Only same-named
              duplicates ask for a pick.
            </Dialog.Description>
          </Dialog.Header>

          <ErrorMessage className="my-4 text-center" error={error} />

          <div className="-mr-3 flex-1 overflow-y-auto pr-3">
            <InvitationCreateForm
              id="invitation-create-form"
              onSubmit={async (data, e) => {
                e.preventDefault()
                const emails = data.sections.flatMap((section) =>
                  section.rows.map((row) => row.Email),
                )
                if (await migratePrompt.intercept(emails, data)) return
                await send(data)
              }}
            />
          </div>

          <Dialog.Footer className="mt-4 flex justify-end gap-3 border-t border-border pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                onOpenChange?.(false)
              }}
            >
              Cancel
            </Button>

            <Button
              type="submit"
              form="invitation-create-form"
              isLoading={loading || migratePrompt.checking}
            >
              {loading ? "Sending..." : "Send Invitations"}
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog>

      <UserMigratePromptDialog
        open={prompt !== null}
        onOpenChange={(next) => {
          if (!next) migratePrompt.dismiss()
        }}
        action="invite"
        users={prompt?.users ?? []}
        remainingCount={remaining ? countRows(remaining) : 0}
        onProceedAnyway={() => {
          if (!prompt) return
          migratePrompt.dismiss()
          void send(prompt.data)
        }}
        onSendActivation={() => {
          migratePrompt.dismiss()
          if (remaining && countRows(remaining) > 0) {
            void send(remaining)
          } else {
            onOpenChange?.(false)
          }
        }}
      />
    </>
  )
}

export default InvitationCreateDialog
